import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box } from '@mui/material';
import { KeyboardEvent, memo, MouseEvent, useCallback, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { quantityValueText } from 'src/components/dispatch/metricText';
import { VEHICLE_CARD_HEIGHT } from 'src/components/dispatch/vehicleCardStyles';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { DispatchCard as DispatchCardType, Vehicle } from 'src/entities/dispatch';
import { GeoAlertInfo } from 'src/utils/geoAlerts';

import { useIsCardSelected, useIsRecentlyMoved } from './BoardInteractionContext';
import { DispatchCardContent } from './DispatchCardContent';
import { DisplayMetric } from './DisplayMetricToggle';


export type DispatchCardProps = {
  card: DispatchCardType;
  isDragging?: boolean;
  showDuplicate?: boolean;
  routeDeviation?: boolean;
  totalCases?: number;
  displayMetrics?: Set<DisplayMetric>;
  /** 会社設定の任意項目名。配車カードは値の詰め元が無いので既定は空 */
  customInputFieldNames?: string[];
  geoAlert?: GeoAlertInfo;
  /** 行が握っている値。カード側で束ねるので、行はインライン closure を作らない */
  vehicle?: Vehicle;
  orderIndex?: number;
  onSelectCard?: (
    card: DispatchCardType,
    vehicle: Vehicle,
    orderIndex: number,
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => void;
};

const selectedRing = {
  boxShadow: '0 0 0 2px hsl(var(--background)), 0 0 0 4px hsl(var(--primary))',
};

// ドラッグドロップ後に「カードがどこに行ったか」を一定時間ハイライトするアニメーション。
// 1.4s × 2 = 2.8s 間、左右の縦バーが primary 色でパルスする (上下は付けない)。
// 既存 boxShadow ('0 1px 2px ...') を維持しつつ、x 方向のみ追加で primary バーを乗せる。
// この 2.8s は store 側のハイライト解除タイマ (boardInteractionStore.ts の
// RECENTLY_MOVED_DURATION_MS) と対の値である。片方だけ変えると、アニメが終わったのに
// 枠が残る / 枠が消えたのにアニメだけ続く のどちらかになる。
const recentlyMovedSx = {
  '@keyframes recentlyMovedPulse': {
    '0%': {
      boxShadow:
        '0 1px 2px 0 rgb(0 0 0 / 0.05), 2px 0 0 0 hsl(var(--primary) / 0.85), -2px 0 0 0 hsl(var(--primary) / 0.85)',
    },
    '60%': {
      boxShadow:
        '0 1px 2px 0 rgb(0 0 0 / 0.05), 3px 0 0 0 hsl(var(--primary) / 0.6), -3px 0 0 0 hsl(var(--primary) / 0.6)',
    },
    '100%': {
      boxShadow:
        '0 1px 2px 0 rgb(0 0 0 / 0.05), 2px 0 0 0 hsl(var(--primary) / 0.4), -2px 0 0 0 hsl(var(--primary) / 0.4)',
    },
  },
  animation: 'recentlyMovedPulse 1.4s ease-out 2',
  boxShadow:
    '0 1px 2px 0 rgb(0 0 0 / 0.05), 2px 0 0 0 hsl(var(--primary) / 0.4), -2px 0 0 0 hsl(var(--primary) / 0.4)',
  position: 'relative' as const,
  zIndex: 1,
};

export const DispatchCardView = memo(function DispatchCardView({
  card,
  isDragging,
  showDuplicate = false,
  routeDeviation = false,
  totalCases,
  displayMetrics,
  customInputFieldNames,
  geoAlert,
  vehicle,
  orderIndex,
  onSelectCard,
}: DispatchCardProps) {
  const trackedOnSelect = useCallback(
    (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => {
      if (!onSelectCard || !vehicle) return;
      ReactGA.event('click', {
        screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
        button_name: '配車カード 選択',
      });
      onSelectCard(card, vehicle, orderIndex ?? 0, event);
    },
    [onSelectCard, vehicle, orderIndex, card]
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.key === 'Enter' || event.key === ' ') && onSelectCard) {
      event.preventDefault();
      trackedOnSelect(event);
    }
  };

  // fr-template は固定ルート由来の「土台」カードで、本物の案件ではないため移動不可。
  // no-delivery / loading 同様 sortable の id 末尾に -nodrag を付け、useSortable も disabled にする。
  const isDraggable =
    card.type === 'delivery' && card.status !== 'no-delivery' && card.status !== 'fr-template';
  const sortableId = isDraggable ? card.id : `${card.id}-nodrag`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortableDragging } = useSortable({
    id: sortableId,
    disabled: !isDraggable,
  });

  // 選択は自分の id だけを購読する（Task 10/11: Presenter の state から store へ移した）。
  // DragOverlay で描くカードも Provider の中に居るが選択の枠は出さないので、isDragging の
  // ときは購読結果を無視する。Rules of Hooks: 早期 return より前で呼ぶ必要があるため、
  // 下の loading 分岐より前に置く（useIsCardSelected は useSyncExternalStore を呼ぶ hook）。
  const selected = useIsCardSelected(card.id) && !isDragging;

  // 直近のドロップで動いたカードのハイライトも自分の id だけを購読する。
  // isDragging で無効化するのは、移設前は行 (VehicleRowCards) が recentlyMoved を
  // prop で渡していて DragOverlay 側は渡していなかった = 掴み直しても DragOverlay の
  // カードにはパルスが出なかったため。見た目を変えないようにここで揃える。
  const recentlyMoved = useIsRecentlyMoved(card.id) && !isDragging;

  // Rules of Hooks: 早期 return の後で useMemo を呼ぶと、card.type が
  // loading ⇄ それ以外に切り替わったとき hook の呼び出し数が変わってしまうため、
  // sx の計算（と、それが読む派生値）は早期 return より前に置く。
  // loading カードには status が無いため、useMemo の依存に使える形として先に取り出す。
  const deliveryStatus = card.type === 'delivery' ? card.status : undefined;
  const dragging = isDragging || isSortableDragging;
  const isTemplate = deliveryStatus === 'template';
  const isFrTemplate = deliveryStatus === 'fr-template';
  const isNoDelivery = deliveryStatus === 'no-delivery';

  const sx = useMemo(() => {
    const highlightSx = isNoDelivery
      ? { bgcolor: 'hsl(var(--card-inactive))', opacity: 0.55, filter: 'grayscale(1)' }
      : isFrTemplate
        // 固定ルートの仮カード (土台): 薄く点線枠。割当済みカードと区別するため影は無し (= 平面表示)。
        ? {
            bgcolor: 'hsl(var(--card))',
            border: '1px dashed hsl(var(--border))',
            opacity: 0.6,
            boxShadow: 'none',
          }
        : routeDeviation
          ? { border: '1px solid hsl(var(--status-warning-accent) / 0.28)', bgcolor: 'hsl(var(--status-warning) / 0.95)' }
          : isTemplate
            ? { bgcolor: 'hsl(var(--card))', border: '1px dashed hsl(var(--border))' }
            : { bgcolor: 'hsl(var(--card))', '&:hover': { border: '1px solid hsl(var(--primary) / 0.3)' } };

    return {
      height: VEHICLE_CARD_HEIGHT,
      width: 160,
      flexShrink: 0,
      borderRadius: '6px',
      p: 1.5,
      cursor: isDraggable ? 'grab' : 'default',
      userSelect: 'none',
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      transition: 'all 0.2s',
      '&:active': isDraggable ? { cursor: 'grabbing' } : {},
      ...highlightSx,
      ...(selected && selectedRing),
      ...(recentlyMoved && recentlyMovedSx),
      ...(dragging && {
        zIndex: 50,
        transform: 'rotate(1deg) scale(1.02)',
        opacity: 0.6,
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraggable, selected, recentlyMoved, dragging, deliveryStatus, routeDeviation]);

  // loading カードは 1 行に 1 枚しか無く、中身も 2 行だけなので分割する利得が無い
  // （Why not: DispatchCardContent へ切り出すと import/型の往復コストだけが増える）。
  if (card.type === 'loading') {
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={trackedOnSelect}
        onKeyDown={handleKeyDown}
        sx={{
          height: VEHICLE_CARD_HEIGHT,
          width: 160,
          flexShrink: 0,
          borderRadius: '6px',
          border: '1px solid hsl(var(--border) / 0.6)',
          borderLeft: '4px solid hsl(var(--card-loading-accent) / 0.5)',
          p: 1.5,
          bgcolor: 'hsl(var(--card-loading) / 0.4)',
          boxShadow: 'none',
          userSelect: 'none',
          transition: 'all 0.2s',
          ...(selected && selectedRing),
          ...(recentlyMoved && recentlyMovedSx),
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            color: 'hsl(var(--card-loading-foreground) / 0.8)',
          }}
        >
          {card.locationName || '積地'}
        </Box>
        <Box
          component="span"
          sx={{
            mt: 1,
            display: 'block',
            fontSize: 12,
            fontFamily: 'monospace',
            fontVariantNumeric: 'tabular-nums',
            color: 'hsl(var(--card-loading-foreground) / 0.65)',
          }}
        >
          {totalCases != null ? `${quantityValueText(totalCases)} cs` : '-- cs'}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={setNodeRef}
      data-testid={`dispatch-card-${card.id}`}
      // 選択の見た目は sx の boxShadow なので、テストから見えるように状態も出す
      // （見た目・a11y は変えない不活性な属性）
      data-selected={selected ? 'true' : 'false'}
      // 直近移動のハイライトも sx の boxShadow + アニメーションなので同じ扱いにする
      data-recently-moved={recentlyMoved ? 'true' : 'false'}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...(isDraggable ? attributes : {})}
      {...(isDraggable ? listeners : {})}
      role="button"
      tabIndex={0}
      onClick={trackedOnSelect}
      onKeyDown={handleKeyDown}
      sx={sx}
    >
      <DispatchCardContent
        card={card}
        showDuplicate={showDuplicate}
        routeDeviation={routeDeviation}
        displayMetrics={displayMetrics}
        customInputFieldNames={customInputFieldNames}
        geoAlert={geoAlert}
      />
    </Box>
  );
});

DispatchCardView.displayName = 'DispatchCardView';
